// draftStore.js — autosave for the calculators. Each calculator's work in
// progress is kept in this browser (localStorage), so a reload, a crash or a
// phone killing the tab in the background doesn't lose it. One draft per
// calculator; + New Quote clears it.
//
// A draft is the calculator's save state plus what it needs to carry on
// exactly where it was: the quote it's amending, the quote it's a copy of,
// and the number it was last saved under. It never touches the Quote Log.
//
// Storage can be unavailable or full (private browsing, Safari limits), so
// every call is wrapped: autosave then just doesn't happen.

const KEY = tab => `sublime_calc_draft_${tab}`;
const VERSION = 1;

export function loadDraft(tab) {
  try {
    const raw = window.localStorage.getItem(KEY(tab));
    if (!raw) return null;
    const d = JSON.parse(raw);
    return d && d.v === VERSION && d.state ? d : null;
  } catch (e) {
    return null;
  }
}

// draft: { state, amendOf, savedRef, savedAsBase, copyOf }
export function saveDraft(tab, draft) {
  try {
    window.localStorage.setItem(KEY(tab), JSON.stringify({ v: VERSION, at: Date.now(), ...draft }));
  } catch (e) { /* storage blocked or full */ }
}

export function clearDraft(tab) {
  try { window.localStorage.removeItem(KEY(tab)); } catch (e) { /* storage blocked */ }
}

// A restored draft as the calculator's starting state (initialState).
export function draftToInitialState(d) {
  if (!d) return null;
  return {
    ...d.state,
    _fromDraft: true,
    _draftAt: d.at,
    _savedRef: d.savedRef || null,
    _savedAsBase: d.savedAsBase || null,
    _copyOf: d.copyOf || "",
  };
}

// Run fn when the page is being hidden or closed. Mobile Safari and Chrome
// often never fire beforeunload (the tab is just frozen or discarded), so
// visibilitychange/pagehide are the reliable last moments to write.
export function onPageHide(fn) {
  const vis = () => { if (document.visibilityState === "hidden") fn(); };
  document.addEventListener("visibilitychange", vis);
  window.addEventListener("pagehide", fn);
  return () => {
    document.removeEventListener("visibilitychange", vis);
    window.removeEventListener("pagehide", fn);
  };
}
